import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Minus, Plus, Trash2, MessageCircle, ShoppingBag, Truck, Check, Percent, Sparkles, Gift, Lock, AlertCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatARS } from "@/lib/api";
import { toast } from "sonner";
import { FREE_SHIPPING_THRESHOLD, WELCOME_DISCOUNT, computeCartPromo } from "@/lib/promos";
import { findSellerByCode } from "@/lib/sellers";

export default function Cart() {
  const { items, updateQty, removeItem, total: subtotal, clear } = useCart();
  const { user, refresh } = useAuth();
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user === undefined) return <div className="p-20 text-center text-slate-500">Cargando...</div>;
  if (!user) return <LoginRequired itemsCount={items.length} />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  // Si al cliente le faltan datos, mostrar form para completar
  if (!user.profile_complete) return <CompleteProfile user={user} onSaved={refresh} />;

  const seller = findSellerByCode(user.seller_code);
  const welcomeEligible = !!user.welcome_discount_available;
  const { missingForShipping, freeShipping, activeTier, nextTier, tierDiscount, welcomeDiscount, welcomeApplied, discount, total } =
    computeCartPromo(subtotal, { welcomeEligible });

  const shippingProgress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);
  const referenceThreshold = nextTier?.threshold || (activeTier?.threshold ?? 0);
  const wholesaleProgress = referenceThreshold ? Math.min(100, (subtotal / referenceThreshold) * 100) : 100;
  const missingForNextTier = nextTier ? Math.max(0, nextTier.threshold - subtotal) : 0;

  const checkout = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!seller) { toast.error("No tenés un vendedor asignado en tu cuenta"); return; }

    setSubmitting(true);
    try {
      await api.post("/orders", {
        customer_name: user.name,
        customer_email: user.email,
        customer_phone: user.phone || "",
        customer_address: user.address,
        customer_city: user.city,
        customer_cuit: user.cuit,
        notes,
        seller_code: user.seller_code,
        seller_name: user.seller_name,
        items: items.map((i) => ({ product_id: i.id, code: i.code || "", name: i.name, price: i.price, quantity: i.quantity })),
        total,
      });

      const productLines = items
        .map((i) => `• *${i.code || i.id.slice(0,6).toUpperCase()}* — ${i.quantity} u — ${i.name}${i.description ? ` (${i.description})` : ""}`)
        .join("\n");
      const msg =
        `*Nuevo pedido - Produlimp BV*\n\n` +
        `*Nombre:* ${user.name}\n` +
        `*Dirección:* ${user.address}\n` +
        `*Ciudad:* ${user.city}\n` +
        `*Email:* ${user.email}\n` +
        `*CUIT:* ${user.cuit}\n` +
        (user.phone ? `*Teléfono:* ${user.phone}\n` : "") +
        `*Vendedor:* ${seller.code} - ${seller.name}\n` +
        `\n*Productos (Código — Cantidad — Descripción):*\n${productLines}\n\n` +
        `*Subtotal:* ${formatARS(subtotal)}\n` +
        (welcomeApplied ? `*Bonus bienvenida (${Math.round(WELCOME_DISCOUNT * 100)}%):* -${formatARS(welcomeDiscount)}\n` : "") +
        (activeTier ? `*Descuento mayorista (${Math.round(activeTier.discount * 100)}%):* -${formatARS(tierDiscount)}\n` : "") +
        (freeShipping ? `*Envío:* GRATIS 🎉\n` : "") +
        `*Total:* ${formatARS(total)}\n` +
        (notes ? `\n*Notas:* ${notes}` : "");

      const url = `https://wa.me/${seller.whatsapp}?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank");
      toast.success("¡Pedido enviado! Completá la orden por WhatsApp.");
      clear();
      refresh();
    } catch {
      toast.error("Error al registrar el pedido. Intentá de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="cart-empty">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
          <ShoppingBag className="w-10 h-10 text-slate-400" />
        </div>
        <h1 className="font-heading font-bold text-2xl mt-6">Tu carrito está vacío</h1>
        <p className="text-slate-500 mt-2">Agregá productos para armar tu pedido.</p>
        <Link to="/shop" className="btn-primary mt-6 inline-flex">Ir a la tienda</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="cart-page">
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mb-2">Tu carrito</h1>
      <p className="text-slate-500 mb-6">Hola <strong>{user.name}</strong>, te atiende <strong className="text-blue-700">{seller?.name || user.seller_name}</strong> (cód. {user.seller_code})</p>

      {welcomeEligible && (
        <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border border-amber-200 rounded-2xl flex items-center gap-3" data-testid="welcome-banner">
          <Gift className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div className="text-sm">
            <strong className="text-amber-900">¡5% OFF de bienvenida!</strong>
            <span className="text-slate-700"> Se aplica automáticamente a tu primer pedido.</span>
          </div>
        </div>
      )}

      <div className="mb-6 grid md:grid-cols-2 gap-4" data-testid="cart-promos">
        <div className={`p-4 rounded-2xl border ${freeShipping ? "bg-green-50 border-green-200" : "bg-white border-slate-200"}`} data-testid="shipping-progress">
          <div className="flex items-center gap-2 mb-2">
            <Truck className={`w-5 h-5 ${freeShipping ? "text-green-600" : "text-slate-500"}`} />
            {freeShipping ? (
              <span className="font-semibold text-green-700 flex items-center gap-1"><Check className="w-4 h-4" /> ¡Tenés envío gratis!</span>
            ) : (
              <span className="text-sm text-slate-700">Te faltan <strong className="text-blue-700">{formatARS(missingForShipping)}</strong> para envío gratis</span>
            )}
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500" style={{ width: `${shippingProgress}%` }} />
          </div>
        </div>

        <div className={`p-4 rounded-2xl border ${activeTier ? "bg-green-50 border-green-200" : "bg-white border-slate-200"}`} data-testid="wholesale-progress">
          <div className="flex items-center gap-2 mb-2">
            <Percent className={`w-5 h-5 ${activeTier ? "text-green-600" : "text-slate-500"}`} />
            {activeTier && !nextTier && (
              <span className="font-semibold text-green-700 flex items-center gap-1"><Sparkles className="w-4 h-4" /> ¡Máximo descuento mayorista! ({Math.round(activeTier.discount * 100)}% OFF)</span>
            )}
            {activeTier && nextTier && (
              <span className="text-sm text-slate-700"><strong className="text-green-700">{Math.round(activeTier.discount * 100)}% OFF</strong> activo · sumá <strong className="text-red-600">{formatARS(missingForNextTier)}</strong> y llevalo a <strong>{Math.round(nextTier.discount * 100)}% OFF</strong></span>
            )}
            {!activeTier && nextTier && (
              <span className="text-sm text-slate-700">Sumá <strong className="text-red-600">{formatARS(missingForNextTier)}</strong> más y obtené <strong>{Math.round(nextTier.discount * 100)}% OFF</strong></span>
            )}
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-red-500 to-green-500 transition-all duration-500" style={{ width: `${wholesaleProgress}%` }} />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex gap-4 items-center" data-testid={`cart-item-${it.id}`}>
              <img src={it.image} alt={it.name} className="w-20 h-20 object-cover rounded-xl bg-slate-50 flex-shrink-0" onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1556228578-8c89e6adf883?q=80&w=400"; }} />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{it.name}</h3>
                {it.code && <p className="text-[11px] font-mono text-blue-700 uppercase">cód. {it.code}</p>}
                <p className="text-slate-500 text-sm">{formatARS(it.price)} c/u</p>
              </div>
              <div className="flex items-center border border-slate-200 rounded-full">
                <button onClick={() => updateQty(it.id, it.quantity - 1)} className="p-2 hover:bg-slate-50 rounded-l-full" data-testid={`cart-dec-${it.id}`}><Minus className="w-3.5 h-3.5" /></button>
                <span className="px-3 text-sm font-semibold min-w-[2rem] text-center">{it.quantity}</span>
                <button onClick={() => updateQty(it.id, it.quantity + 1)} className="p-2 hover:bg-slate-50 rounded-r-full" data-testid={`cart-inc-${it.id}`}><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <div className="font-bold w-24 text-right">{formatARS(it.price * it.quantity)}</div>
              <button onClick={() => removeItem(it.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-full" data-testid={`cart-remove-${it.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <aside className="bg-white rounded-2xl border border-slate-200 p-6 h-fit sticky top-28">
          <h2 className="font-heading font-bold text-xl mb-2">Finalizar pedido</h2>

          {/* Datos del cliente para el envío */}
          <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 mb-3 space-y-0.5">
            <div><strong className="text-slate-700">Nombre:</strong> {user.name}</div>
            <div><strong className="text-slate-700">Dirección:</strong> {user.address}</div>
            <div><strong className="text-slate-700">Ciudad:</strong> {user.city}</div>
            <div><strong className="text-slate-700">CUIT:</strong> {user.cuit}</div>
            <div><strong className="text-slate-700">Email:</strong> {user.email}</div>
          </div>

          <form onSubmit={checkout} className="space-y-3">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas / aclaraciones (opcional)" rows={2} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="checkout-notes" />

            <div className="pt-3 border-t border-slate-100 space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{formatARS(subtotal)}</span>
              </div>
              {welcomeApplied && (
                <div className="flex justify-between text-amber-700 font-medium" data-testid="cart-welcome-row">
                  <span className="flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Bonus bienvenida ({Math.round(WELCOME_DISCOUNT * 100)}%)</span>
                  <span>-{formatARS(welcomeDiscount)}</span>
                </div>
              )}
              {activeTier && (
                <div className="flex justify-between text-green-700 font-medium" data-testid="cart-discount-row">
                  <span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Descuento mayorista ({Math.round(activeTier.discount * 100)}%)</span>
                  <span>-{formatARS(tierDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-600">
                <span>Envío</span>
                <span className={freeShipping ? "text-green-700 font-semibold" : ""}>{freeShipping ? "GRATIS" : "A coordinar"}</span>
              </div>
            </div>
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-100">
              <span>Total</span><span className="text-green-700" data-testid="cart-total">{formatARS(total)}</span>
            </div>

            <button type="submit" disabled={submitting || !seller} className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-all bg-green-600 hover:bg-green-700 text-white disabled:bg-slate-300 disabled:cursor-not-allowed" data-testid="checkout-whatsapp-btn">
              <MessageCircle className="w-4 h-4" /> {submitting ? "Enviando..." : "Enviar pedido por WhatsApp"}
            </button>
            <p className="text-xs text-slate-500 text-center">Tu pedido va al vendedor <strong>{seller?.name}</strong></p>
          </form>
        </aside>
      </div>
    </div>
  );
}

function LoginRequired({ itemsCount }) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="cart-login-required">
      <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto">
        <Lock className="w-10 h-10 text-blue-600" />
      </div>
      <h1 className="font-heading font-bold text-2xl mt-6">Iniciá sesión para ver tu carrito</h1>
      <p className="text-slate-500 mt-2">Para hacer un pedido necesitás tener una cuenta con un vendedor asignado. {itemsCount > 0 && `Tenés ${itemsCount} producto(s) esperando.`}</p>
      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <Link to="/login" className="btn-primary bg-green-600 hover:bg-green-700">Ingresar</Link>
        <Link to="/register" className="btn-outline">Crear cuenta gratis</Link>
      </div>
    </div>
  );
}

function CompleteProfile({ user, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    address: user.address || "",
    city: user.city || "",
    cuit: user.cuit || "",
    phone: user.phone || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address.trim() || !form.city.trim() || !form.cuit.trim()) {
      toast.error("Completá todos los campos obligatorios"); return;
    }
    setSaving(true);
    try {
      await api.patch("/customers/me", form);
      await onSaved();
      toast.success("Datos guardados. Ya podés finalizar tu pedido.");
    } catch {
      toast.error("Error al guardar los datos");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12" data-testid="complete-profile">
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-full bg-amber-50 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl">Completá tus datos</h1>
            <p className="text-sm text-slate-500">Necesitamos algunos datos para procesar tu pedido y coordinar el envío.</p>
          </div>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-700">Nombre (empresa / supermercado) *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="cp-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Dirección *</label>
            <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Calle y número" className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="cp-address" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Ciudad *</label>
              <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="cp-city" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">CUIT *</label>
              <input required value={form.cuit} onChange={(e) => setForm({ ...form, cuit: e.target.value })} placeholder="20-12345678-9" className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="cp-cuit" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Teléfono</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-400 outline-none" data-testid="cp-phone" />
          </div>
          <button type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-3 font-semibold transition-colors disabled:bg-slate-300 mt-2" data-testid="cp-save">
            {saving ? "Guardando..." : "Guardar y continuar"}
          </button>
        </form>
      </div>
    </div>
  );
}
